import { MenuItem, Tooltip, styled } from "@mui/material";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import ColumnSetup from "../../../Shared/ColumnSetup/ColumnSetup";
import PermissionComponent, {
  getPermissionDisplayName,
} from "../../../Shared/Common/Permissions/PermissionComponent";
import ArrowBottomIcon from "../../../Shared/SVG/ArrowBottomIcon";
import BreadCrumbs from "../../../Utils/Common/Breadcrumb";
import { emptyObjectValues } from "../../../Utils/Common/Requisition";
import { AutocompleteStyle } from "../../../Utils/MuiStyles/AutocompleteStyles";
import {
  StyledDropButton,
  StyledDropMenu,
} from "../../../Utils/Style/Dropdownstyle";
import { useInterpretationContext } from "./InterpretationContext";
import ReqDataGrid from "./InterpretationGrid";
import RejectModal from "./RejectModal";
import usePagination from "Shared/hooks/usePagination";
import useLang from "Shared/hooks/useLanguage";
import useIsMobile from "Shared/hooks/useIsMobile";
import CustomPagination from "Shared/JsxPagination";
import {
  Approve,
  BulkReRunPGxLISOrders,
  BulkReSendPGxLISOrders,
} from "Services/PGXLis/Interpretation";

const TabSelected = styled(Tab)(AutocompleteStyle());
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 0 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}
function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
const Interpretation = () => {
  const {
    data,
    filterData,
    setFilterData,
    searchValue,
    setSearchValue,
    selectedBox,
    setSelectedBox,
    loadGridData,
    apiCalls,
    loading,
    GetPrintersInfo,
    total,
    value,
    setValue,
    setRowsToExpand,
    setIsMasterExpandTriggered,
  } = useInterpretationContext();

  const { t } = useLang();
  const isMobile = useIsMobile();

  const [show, setShow] = useState(false);
  const [filters, setFilters] = useState<any>([]);
  const [resetClicked, setResetClicked] = useState(false);
  const [showSetupModal, setShowModalSetup] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(false);
  const [searchedTags, setSearchedTags] = useState<string[]>([]);
  const [isInitialRender2, setIsInitialRender2] = useState(false);
  const [triggerSearchData, setTriggerSearchData] = useState<boolean>(false);
  //============================================================================================
  //====================================  PAGINATION START =====================================
  //============================================================================================

  const {
    curPage,
    nextPage,
    pageNumbers,
    pageSize,
    prevPage,
    setCurPage,
    setPageSize,
    setTotal,
    showPage,
    totalPages,
  } = usePagination();

  useEffect(() => {
    setTotal(total);
  }, [total]);

  useEffect(() => {
    setRowsToExpand([]);
    setIsMasterExpandTriggered(false);
  }, [curPage, pageSize]);

  useEffect(() => {
    filterData.pageNumber = curPage;
    filterData.pageSize = pageSize;
    if (isInitialRender2) {
      loadGridData(true);
    } else {
      setIsInitialRender2(true);
    }
  }, [pageSize, curPage, triggerSearchData]);

  //============================================================================================
  //====================================  PAGINATION END =======================================
  //============================================================================================

  const resetFilterData = () => {
    filterData.filters = [];
    filterData.sortColumn = "";
    filterData.pageSize = 50;
    filterData.pageNumber = 1;
    filterData.sortDirection = "";
  };
  const getInitialApiData = async () => {
    await Promise.all([apiCalls(), GetPrintersInfo()]);
  };

  const [anchorEl, setAnchorEl] = React.useState({
    dropdown1: null,
    dropdown2: null,
    dropdown3: null,
    dropdown4: null,
  });
  const openDrop =
    Boolean(anchorEl.dropdown1) ||
    Boolean(anchorEl.dropdown2) ||
    Boolean(anchorEl.dropdown3) ||
    Boolean(anchorEl.dropdown4);

  const handleClick = (event: any, dropdownName: any) => {
    setAnchorEl({ ...anchorEl, [dropdownName]: event.currentTarget });
  };

  const handleClose = () => {
    setShowModalSetup(false);
  };

  const handleApprove = () => {
    if (!selectedBox.ids.length) {
      toast.error(t("Please select at least one record"));
      return;
    }
    approveMutation.mutate();
  };

  const handleBulkRerun = () => {
    if (!selectedBox.ids.length) {
      toast.error(t("Please select at least one record"));
      return;
    }
    bulkRerunMutation.mutate();
  };

  const handleBulkResend = () => {
    if (!selectedBox.ids.length) {
      toast.error(t("Please select at least one record"));
      return;
    }
    bulkResendMutation.mutate();
  };

  const approveMutation = useMutation({
    mutationFn: async () => {
      if (selectedBox?.ids?.length === 0) {
        toast.error(t("Please select atleast one record"));
        return;
      }
      const pendingList: any[] = [];

      selectedBox.ids.forEach((record: any) => {
        const foundRecord = data?.gridData?.find(
          (item: any) => item.RequisitionOrderID === record.requisitionOrderId
        );

        if (!foundRecord?.resultFile) {
          pendingList.push(foundRecord);
        }
      });

      if (pendingList.length > 0) {
        toast.error("Files are missing against the given record.");
        throw new Error("Validation failed");
      }
      const res = await Approve(selectedBox?.ids);
      return res;
    },

    onSuccess: (res) => {
      if (res?.data?.statusCode === 200) {
        setSelectedBox({ ids: [] });
        loadGridData(true);
        toast.success(t(res?.data?.message));
      } else {
        toast.error(t(res?.data?.message));
      }
    },

    onError: (error: any) => {
      console.error(error, "bulk request failure");
    },
  });

  const bulkRerunMutation = useMutation({
    mutationFn: async () => {
      const reqOrderIds = selectedBox.ids.map(
        (item: any) => item.requisitionOrderId
      );
      const res = await BulkReRunPGxLISOrders(reqOrderIds);
      return res;
    },

    onSuccess: (res) => {
      if (res?.data?.statusCode === 200) {
        setSelectedBox({ ids: [] });
        loadGridData(true);
        toast.success(t(res?.data?.message));
      } else {
        toast.error(t(res?.data?.message));
      }
    },

    onError: (error: any) => {
      console.error(error, "bulk rerun failure");
      toast.error("Request failed");
    },
  });

  const bulkResendMutation = useMutation({
    mutationFn: async () => {
      const reqOrderIds = selectedBox.ids.map(
        (item: any) => item.requisitionOrderId
      );
      const res = await BulkReSendPGxLISOrders(reqOrderIds);
      return res;
    },

    onSuccess: (res) => {
      if (res?.data?.statusCode === 200) {
        setSelectedBox({ ids: [] });
        loadGridData(true);
        toast.success(t(res?.data?.message));
      } else {
        toast.error(t(res?.data?.message));
      }
    },

    onError: (error: any) => {
      console.error(error, "bulk resend failure");
      toast.error("Request failed");
    },
  });

  const handleChange = async (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    const Id: any = event.currentTarget.id;
    const thenum: any = Id.match(/\d+/)[0];
    setValue(newValue);
    if (filterData?.tabId !== thenum) {
      const emptySearchObj = emptyObjectValues(searchValue);
      setSearchValue(emptySearchObj);
      filterData.tabId = parseInt(thenum);
      setCurPage(1);
      setRowsToExpand([]);
      filterData.filters = [];
      filterData.pageNumber = 1;
      setFilters([]);
      await loadGridData();
    }
    setSelectedBox({ ids: [] });
  };

  // Code for Bulk action for Pending
  const handleCloseAnchor = (dropdownName: string) => {
    setAnchorEl({ ...anchorEl, [dropdownName]: null });
  };

  const handleSearch = () => {
    setCurPage(1);
    setTriggerSearchData((prev) => !prev);
  };

  const resetSearch = () => {
    setSelectedBox({ ids: [] });
    resetFilterData();
    setFilters([]);
    setResetClicked(!resetClicked);
    const emptySearchObj = emptyObjectValues(searchValue);
    setSearchValue(emptySearchObj);
    loadGridData(true);
  };

  const handleClickOpen = () => {
    if (selectedBox.ids.length === 0) {
      toast.error(t("Please select atleast one record"));
    } else {
      setShow(true);
      handleCloseAnchor("dropdown1");
    }
  };

  // Handling searchedTags
  const handleTagRemoval = (clickedTag: string) => {
    const resultedTab = filters.filter((tab: any) => {
      return tab.label !== clickedTag;
    });

    const resultedTabs = filterData.filters.filter((tab: any) => {
      return tab.label !== clickedTag;
    });

    setFilters(() => [...resultedTab]);
    setSearchValue((prevValue: any) => ({
      ...prevValue,
      filters: resultedTab,
    }));
    setFilterData((prevValue: any) => ({
      ...prevValue,
      filters: resultedTabs,
    }));
  };

  useEffect(() => {
    const filteredObject: any = {};
    filters?.forEach((filterData: any) => {
      filteredObject[filterData.label] = filterData.filterValue;
    });

    const uniqueKeys = new Set<string>();
    for (const [key, value] of Object.entries(filteredObject)) {
      if (value) {
        uniqueKeys.add(key);
      }
    }
    setSearchedTags(Array.from(uniqueKeys));
  }, [filters]);

  useEffect(() => {
    if (isInitialRender) {
      if (searchedTags.length === 0) {
        resetSearch();
      }
    } else {
      setIsInitialRender(true);
    }
  }, [searchedTags.length]);

  useEffect(() => {
    filterData.tabId = 1;
    getInitialApiData();
  }, []);

  return (
    <>
      <RejectModal
        show={show}
        setShow={setShow}
        selectedRecord={selectedBox.ids.map(
          (item: any) => item.requisitionOrderId
        )}
        loadGridData={loadGridData}
        setSelectedBox={setSelectedBox}
      />
      <ColumnSetup
        show={showSetupModal}
        closeSetupModal={handleClose}
        loadData={apiCalls}
        value={filterData.tabId}
        columnsToUse={data.gridHeaders[value]?.tabHeaders}
      />

      <div className="d-flex flex-column flex-column-fluid">
        <div className="app-toolbar py-3 py-lg-3">
          <div className="app-container container-fluid d-flex flex-wrap gap-4 justify-content-center justify-content-sm-between align-items-center">
            <BreadCrumbs />
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <Tooltip title="Setup" arrow placement="top">
                <button
                  className="btn btn-icon btn-sm fw-bold btn-setting btn-icon-light"
                  onClick={() => setShowModalSetup(true)}
                >
                  <i className="fa fa-gear"></i>
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
        <div className="d-flex flex-column flex-column-fluid">
          <div className="app-content flex-column-fluid">
            <div className="app-container container-fluid">
              <Tabs
                value={value}
                onChange={handleChange}
                TabIndicatorProps={{ style: { background: "transparent" } }}
                className="min-h-auto"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  "& .MuiTabs-scrollButtons": {
                    width: 0,
                    transition: "width 0.7s ease",
                    "&:not(.Mui-disabled)": {
                      width: "48px",
                    },
                  },
                }}
              >
                {Array.isArray(data.gridHeaders) &&
                  data?.gridHeaders?.map((items: any) => (
                    <TabSelected
                      data-test-id={items?.tabName?.replace(/\s/g, "")}
                      key={items.tabID}
                      label={t(items.tabName)}
                      {...a11yProps(items.tabID)}
                      className="fw-bold text-capitalize"
                      disabled={loading}
                    />
                  ))}
              </Tabs>
              <div className="card tab-content-card">
                <div className="card-body py-2">
                  <div className="d-flex gap-4 flex-wrap mb-1">
                    {searchedTags.map((tag) => (
                      <div
                        className="d-flex align-items-center cursor-pointer gap-1 p-2 rounded bg-light"
                        onClick={() => handleTagRemoval(tag)}
                        key={tag}
                      >
                        <span className="fw-bold">{tag}</span>
                        <i className="bi bi-x"></i>
                      </div>
                    ))}
                  </div>
                  <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mb-2 col-12 responsive-flexed-actions">
                    <div className="d-flex gap-2 responsive-flexed-actions">
                      <div className="d-flex align-items-center">
                        <span className="fw-400 mr-3">{t("Records")}</span>
                        <select
                          id={`BloodResultDataRecords`}
                          className="form-select w-125px h-33px rounded py-2"
                          data-kt-select2="true"
                          data-placeholder="Select option"
                          data-dropdown-parent="#kt_menu_63b2e70320b73"
                          data-allow-clear="true"
                          onChange={async (e) => {
                            const value = parseInt(e.target.value);
                            setPageSize(value);
                            filterData.pageSize = value;
                            await loadGridData();
                          }}
                          value={filterData.pageSize}
                        >
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                          <option value="150">150</option>
                          <option value="200">200</option>
                        </select>
                      </div>

                      <div className="d-flex justify-content-center gap-2">
                        {(filterData.tabId === 1 || filterData.tabId === 2) &&
                        !isMobile ? (
                          <>
                            <StyledDropButton
                              id={`BloodResultDataBulkAction`}
                              aria-controls={
                                openDrop ? "demo-positioned-menu1" : undefined
                              }
                              aria-haspopup="true"
                              aria-expanded={openDrop ? "true" : undefined}
                              onClick={(event: any) =>
                                handleClick(event, "dropdown1")
                              }
                              className="btn btn-info btn-sm"
                            >
                              {t("Bulk Action")}
                              <span className="svg-icon svg-icon-5 m-0">
                                <ArrowBottomIcon />
                              </span>
                            </StyledDropButton>
                            <StyledDropMenu
                              id={`BloodResultDataBulkActionMenu`}
                              aria-labelledby="demo-positioned-button1"
                              anchorEl={anchorEl.dropdown1}
                              open={Boolean(anchorEl.dropdown1)}
                              onClose={() => handleCloseAnchor("dropdown1")}
                              sx={{
                                padding: 0,
                              }}
                              anchorOrigin={{
                                vertical: "top",
                                horizontal: "left",
                              }}
                              transformOrigin={{
                                vertical: "top",
                                horizontal: "left",
                              }}
                            >
                              {filterData.tabId === 1 ? (
                                <>
                                  <PermissionComponent
                                    moduleName="PGX LIS"
                                    pageName="Interpretation"
                                    permissionIdentifier="BulkApprove"
                                  >
                                    {({
                                      pageName,
                                      moduleName,
                                      permissionIdentifier,
                                    }) => (
                                      <MenuItem className=" p-0">
                                        <a
                                          id={`BloodResultDataBulkReport`}
                                          className="w-100 text-dark"
                                          onClick={() => {
                                            handleCloseAnchor("dropdown1");
                                            handleApprove();
                                          }}
                                        >
                                          {getPermissionDisplayName(
                                            pageName,
                                            moduleName,
                                            permissionIdentifier,
                                            "Approve"
                                          )}
                                        </a>
                                      </MenuItem>
                                    )}
                                  </PermissionComponent>
                                  <PermissionComponent
                                    moduleName="PGX LIS"
                                    pageName="Interpretation"
                                    permissionIdentifier="BulkReject"
                                  >
                                    {({
                                      pageName,
                                      moduleName,
                                      permissionIdentifier,
                                    }) => (
                                      <MenuItem className="p-0">
                                        <a
                                          id={`BloodResultDataBulkReport`}
                                          className="w-100 text-dark"
                                          onClick={handleClickOpen}
                                        >
                                          {getPermissionDisplayName(
                                            pageName,
                                            moduleName,
                                            permissionIdentifier,
                                            "Reject"
                                          )}
                                        </a>
                                      </MenuItem>
                                    )}
                                  </PermissionComponent>
                                </>
                              ) : null}
                              <PermissionComponent
                                moduleName="PGX LIS"
                                pageName="Interpretation"
                                permissionIdentifier="BulkRerun"
                              >
                                {({
                                  pageName,
                                  moduleName,
                                  permissionIdentifier,
                                }) => (
                                  <MenuItem className="p-0">
                                    <a
                                      id={`BloodResultDataBulkRerun`}
                                      className="w-100 text-dark"
                                      onClick={() => {
                                        handleCloseAnchor("dropdown1");
                                        handleBulkRerun();
                                      }}
                                    >
                                      {getPermissionDisplayName(
                                        pageName,
                                        moduleName,
                                        permissionIdentifier,
                                        "Rerun"
                                      )}
                                    </a>
                                  </MenuItem>
                                )}
                              </PermissionComponent>
                              {filterData.tabId === 2 ? (
                                <PermissionComponent
                                  moduleName="PGX LIS"
                                  pageName="Interpretation"
                                  permissionIdentifier="BulkResend"
                                >
                                  {({
                                    pageName,
                                    moduleName,
                                    permissionIdentifier,
                                  }) => (
                                    <MenuItem className="p-0">
                                      <a
                                        id={`BloodResultDataBulkResend`}
                                        className="w-100 text-dark"
                                        onClick={() => {
                                          handleCloseAnchor("dropdown1");
                                          handleBulkResend();
                                        }}
                                      >
                                        {getPermissionDisplayName(
                                          pageName,
                                          moduleName,
                                          permissionIdentifier,
                                          "Resend"
                                        )}
                                      </a>
                                    </MenuItem>
                                  )}
                                </PermissionComponent>
                              ) : null}
                            </StyledDropMenu>
                          </>
                        ) : null}
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                      {(filterData.tabId === 1 || filterData.tabId === 2) &&
                      isMobile ? (
                        <StyledDropButton
                          id={`BloodResultDataBulkAction`}
                          aria-controls={
                            openDrop ? "demo-positioned-menu1" : undefined
                          }
                          aria-haspopup="true"
                          aria-expanded={openDrop ? "true" : undefined}
                          onClick={(event: any) =>
                            handleClick(event, "dropdown1")
                          }
                          className="btn btn-info btn-sm"
                        >
                          {t("Bulk Action")}
                          <span className="svg-icon svg-icon-5 m-0">
                            <ArrowBottomIcon />
                          </span>
                        </StyledDropButton>
                      ) : null}
                      <StyledDropMenu
                        id={`BloodResultDataBulkActionMenu`}
                        aria-labelledby="demo-positioned-button1"
                        anchorEl={anchorEl.dropdown1}
                        open={Boolean(anchorEl.dropdown1)}
                        onClose={() => handleCloseAnchor("dropdown1")}
                        sx={{
                          padding: 0,
                        }}
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                      >
                        {filterData.tabId === 1 ? (
                          <>
                            <PermissionComponent
                              moduleName="PGX LIS"
                              pageName="Interpretation"
                              permissionIdentifier="BulkApprove"
                            >
                              {({
                                pageName,
                                moduleName,
                                permissionIdentifier,
                              }) => (
                                <MenuItem className=" p-0">
                                  <a
                                    id={`BloodResultDataBulkReport`}
                                    className="w-100 text-dark"
                                    onClick={() => {
                                      handleCloseAnchor("dropdown1");
                                      handleApprove();
                                    }}
                                  >
                                    {getPermissionDisplayName(
                                      pageName,
                                      moduleName,
                                      permissionIdentifier,
                                      "Approve"
                                    )}
                                  </a>
                                </MenuItem>
                              )}
                            </PermissionComponent>
                            <PermissionComponent
                              moduleName="PGX LIS"
                              pageName="Interpretation"
                              permissionIdentifier="BulkReject"
                            >
                              {({
                                pageName,
                                moduleName,
                                permissionIdentifier,
                              }) => (
                                <MenuItem className="p-0">
                                  <a
                                    id={`BloodResultDataBulkReport`}
                                    className="w-100 text-dark"
                                    onClick={handleClickOpen}
                                  >
                                    {getPermissionDisplayName(
                                      pageName,
                                      moduleName,
                                      permissionIdentifier,
                                      "Reject"
                                    )}
                                  </a>
                                </MenuItem>
                              )}
                            </PermissionComponent>
                          </>
                        ) : null}
                        <PermissionComponent
                          moduleName="PGX LIS"
                          pageName="Interpretation"
                          permissionIdentifier="BulkRerun"
                        >
                          {({ pageName, moduleName, permissionIdentifier }) => (
                            <MenuItem className="p-0">
                              <a
                                id={`BloodResultDataBulkRerun`}
                                className="w-100 text-dark"
                                onClick={() => {
                                  handleCloseAnchor("dropdown1");
                                  handleBulkRerun();
                                }}
                              >
                                {getPermissionDisplayName(
                                  pageName,
                                  moduleName,
                                  permissionIdentifier,
                                  "Rerun"
                                )}
                              </a>
                            </MenuItem>
                          )}
                        </PermissionComponent>
                        {filterData.tabId === 2 ? (
                          <PermissionComponent
                            moduleName="PGX LIS"
                            pageName="Interpretation"
                            permissionIdentifier="BulkResend"
                          >
                            {({
                              pageName,
                              moduleName,
                              permissionIdentifier,
                            }) => (
                              <MenuItem className="p-0">
                                <a
                                  id={`BloodResultDataBulkResend`}
                                  className="w-100 text-dark"
                                  onClick={() => {
                                    handleCloseAnchor("dropdown1");
                                    handleBulkResend();
                                  }}
                                >
                                  {getPermissionDisplayName(
                                    pageName,
                                    moduleName,
                                    permissionIdentifier,
                                    "Resend"
                                  )}
                                </a>
                              </MenuItem>
                            )}
                          </PermissionComponent>
                        ) : null}
                      </StyledDropMenu>

                      <button
                        id={`BloodResultDataSearch`}
                        onClick={() => handleSearch()}
                        className="btn btn-linkedin btn-sm fw-500"
                        aria-controls="Search"
                      >
                        {t("Search")}
                      </button>
                      <button
                        onClick={resetSearch}
                        type="button"
                        className="btn btn-secondary btn-sm btn-secondary--icon fw-500"
                        id={`BloodResultDataReset`}
                      >
                        <span>
                          <span>{t("Reset")}</span>
                        </span>
                      </button>
                    </div>
                  </div>
                  <TabPanel value={value} index={value}>
                    <ReqDataGrid
                      tabsInfo={data.gridHeaders[value]?.tabHeaders}
                      value={value}
                      filters={filters}
                      setFilters={setFilters}
                      setCurPage={setCurPage}
                    />
                  </TabPanel>
                  {loading ? null : (
                    <CustomPagination
                      curPage={curPage}
                      nextPage={nextPage}
                      pageNumbers={pageNumbers}
                      pageSize={pageSize}
                      prevPage={prevPage}
                      showPage={showPage}
                      total={total}
                      totalPages={totalPages}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(Interpretation);
